'use client';

import { useEffect } from 'react';
import { WEB_VITALS_SAMPLE_RATE } from '@/constants';
import type { WebVitalsBeacon, WebVitalsBeaconMetric, WebVitalName } from '@/src/types/webVitals';

// Web Vitals RUM (ACEB-219 item 22). Collects TTFB/FCP/LCP/CLS/INP with
// attribution for the initial page load and beacons them to /api/vitals,
// which relays server-side to PostHog. The web-vitals lib (~2 KB) is
// dynamically imported after idle - same discipline as posthog-lazy: nothing
// is added to any page's first-load JS. See docs/features/web-vitals-rum.md.

const IDLE_TIMEOUT_MS = 3000;
const NO_IDLE_FALLBACK_MS = 2000;
const MAX_ATTRIBUTION_STRING = 120;

let started = false;

// Keep numbers and short strings; drop entry objects (navigationEntry,
// lcpEntry, …) so the beacon stays small no matter the web-vitals version.
function slimAttribution(attribution: object | undefined): Record<string, number | string> {
  const out: Record<string, number | string> = {};
  if (!attribution) return out;
  for (const [key, value] of Object.entries(attribution)) {
    if (typeof value === 'number' && Number.isFinite(value)) {
      out[key] = Math.round(value * 10) / 10;
    } else if (typeof value === 'string' && value) {
      out[key] = value.slice(0, MAX_ATTRIBUTION_STRING);
    }
  }
  return out;
}

function send(body: WebVitalsBeacon): void {
  const blob = new Blob([JSON.stringify(body)], { type: 'application/json' });
  try {
    if (navigator.sendBeacon?.('/api/vitals', blob)) return;
  } catch {
    // fall through to fetch
  }
  void fetch('/api/vitals', { method: 'POST', body: blob, keepalive: true }).catch(() => {});
}

function start(): void {
  if (started) return;
  started = true;
  if (Math.random() >= WEB_VITALS_SAMPLE_RATE) return;

  // Metrics describe the initial (hard) navigation, so pin its pathname now -
  // soft navigations later must not relabel them.
  const path = window.location.pathname;
  const locale = document.documentElement.lang || 'en';
  const queued = new Map<string, WebVitalsBeaconMetric>();

  void import('web-vitals/attribution')
    .then(({ onTTFB, onFCP, onLCP, onCLS, onINP }) => {
      const queue = (metric: {
        name: WebVitalName;
        value: number;
        rating: WebVitalsBeaconMetric['rating'];
        id: string;
        navigationType?: string;
        attribution: object;
      }) => {
        queued.set(metric.id, {
          name: metric.name,
          value: Math.round(metric.value * 1000) / 1000,
          rating: metric.rating,
          id: metric.id,
          navigationType: metric.navigationType ?? 'navigate',
          attribution: slimAttribution(metric.attribution),
        });
      };
      onTTFB(queue);
      onFCP(queue);
      onLCP(queue);
      onCLS(queue);
      onINP(queue);
    })
    .catch(() => {
      // ad blocker / failed chunk: no RUM this pageview
    });

  // Flush-on-hidden is the web-vitals-documented reliable pattern: LCP/CLS/INP
  // finalize right before visibility turns hidden, so their callbacks have
  // already queued by the time this listener runs.
  const flush = () => {
    if (queued.size === 0) return;
    const nav = performance.getEntriesByType('navigation')[0] as
      | PerformanceNavigationTiming
      | undefined;
    const connection = (navigator as { connection?: { effectiveType?: string } }).connection;
    send({
      path,
      locale,
      connection: connection?.effectiveType ?? null,
      redirectCount: nav ? nav.redirectCount : null,
      redirectDuration: nav ? Math.round(nav.redirectEnd - nav.redirectStart) : null,
      metrics: [...queued.values()],
    });
    queued.clear();
  };

  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') flush();
  });
  window.addEventListener('pagehide', flush);
}

export function WebVitalsReporter() {
  useEffect(() => {
    const idle = (
      window as unknown as {
        requestIdleCallback?: (cb: () => void, opts?: { timeout: number }) => void;
      }
    ).requestIdleCallback;
    if (idle) {
      idle(start, { timeout: IDLE_TIMEOUT_MS });
    } else {
      setTimeout(start, NO_IDLE_FALLBACK_MS);
    }
  }, []);
  return null;
}
